import { useCallback, useEffect, useRef, useState } from "react";
import { getErrorMessage } from "@/core/api/errors";
import { dayjs } from "@/core/utils/date";
import {
  getAiScriptExplanationChatPath,
  isChatAccessDeniedError,
  useCreateAiScriptExplanationChat,
  useFollowAiScriptExplanationChatTurn,
  useGetAiScriptExplanationChatThread,
  useGetAiScriptExplanationChatThreads,
} from "@/payroll/ai-script/query";
import { AI_CHAT_MESSAGE_ROLE } from "@/payroll/ai-script/types";
import type { ExplanationChatPathBuilder } from "@/payroll/ai-script/types";
import type {
  AiScriptChatSelectionContext,
  IAiScriptExplanationChatPersistedMessage,
  IAiScriptExplanationChatResponse,
} from "@/payroll/ai-script/types";
import {
  formatSelectionContextSentence,
  hasSelectionContextContent,
} from "./selection-context";
import type { ChatMessage, PendingChatResponse } from "./types";

const CHAT_VIEW = {
  list: "list",
  chat: "chat",
} as const;

const THREAD_LIST_POLL_INTERVAL_MS = 3000;

interface ThreadLiveState {
  targetText: string;
  displayedText: string;
  progressSteps: string[];
  pendingResponse: PendingChatResponse;
  isFollowing: boolean;
}

const emptyThreadLiveState = (): ThreadLiveState => ({
  targetText: "",
  displayedText: "",
  progressSteps: [],
  pendingResponse: null,
  isFollowing: false,
});

const removeThreadTurn = (
  turnsByThreadId: Record<string, string>,
  threadId: string,
) => {
  const { [threadId]: _removed, ...remainingTurns } = turnsByThreadId;
  return remainingTurns;
};

const appendProgressStep = (steps: string[], label: string) =>
  steps[steps.length - 1] === label ? steps : [...steps, label];

const buildMessageId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${dayjs().valueOf()}-${Math.random()}`;

// Persisted user messages carry the attached-selection sentence; the chip is
// the in-session indicator, so strip the sentence when rendering history.
const stripSelectionContextSentence = (content: string) =>
  content.replace(/\n\n\(I'm looking at [\s\S]*\)$/, "");

const mapPersistedMessage = (
  message: IAiScriptExplanationChatPersistedMessage,
): ChatMessage => {
  const isError = message.role === AI_CHAT_MESSAGE_ROLE.error;

  return {
    id: message.id,
    role: message.role === AI_CHAT_MESSAGE_ROLE.user ? "user" : "assistant",
    content:
      message.role === AI_CHAT_MESSAGE_ROLE.user
        ? stripSelectionContextSentence(message.content)
        : message.content,
    sources: message.metadata?.sources ?? undefined,
    error: isError
      ? (message.metadata?.error?.message ?? message.content)
      : null,
  };
};

const buildAssistantResponseMessage = (
  response: IAiScriptExplanationChatResponse,
  progressSteps: string[],
): ChatMessage => ({
  id: buildMessageId(),
  role: "assistant",
  content: response.message,
  steps: progressSteps,
  sources: response.sources,
  error: response.error?.message ?? null,
});

const buildAssistantErrorMessage = (message: string): ChatMessage => ({
  id: buildMessageId(),
  role: "assistant",
  content: message,
  error: message,
});

export const useAiScriptExplanationChatSession = ({
  businessId,
  runId,
  selectionContext,
  pendingQuestion,
  onQuestionPrepared,
  // Which chat this session talks to. Defaults to a payroll run's explanation
  // chat; profitability reports pass their own root and reuse everything else.
  buildChatPath = getAiScriptExplanationChatPath,
}: {
  businessId: string | null;
  runId: string;
  selectionContext?: AiScriptChatSelectionContext | null;
  pendingQuestion?: string | null;
  onQuestionPrepared?: () => void;
  buildChatPath?: ExplanationChatPathBuilder;
}) => {
  const [view, setView] = useState<(typeof CHAT_VIEW)[keyof typeof CHAT_VIEW]>(
    CHAT_VIEW.list,
  );
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [liveStatesByThreadId, setLiveStatesByThreadId] = useState<
    Record<string, ThreadLiveState>
  >({});
  const [localRunningTurnsByThreadId, setLocalRunningTurnsByThreadId] =
    useState<Record<string, string>>({});
  const [isStartingNewTurn, setIsStartingNewTurn] = useState(false);
  const followedTurnKeysRef = useRef(new Set<string>());
  const selectedThreadIdRef = useRef<string | null>(null);
  // Tracks which thread's persisted history is loaded into local state, so
  // background refetches never clobber a richer in-session transcript.
  const loadedThreadIdRef = useRef<string | null>(null);
  // Jump straight into a new chat the first time the panel opens with no
  // existing threads.
  const hasAutoEnteredChatRef = useRef(false);
  // Whether the user is pinned to the bottom of the transcript; streamed
  // content only auto-follows while pinned, so scrolling up to read is never
  // snapped back down.
  const wasAtBottomRef = useRef(true);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const { mutate: sendMessage } = useCreateAiScriptExplanationChat(
    businessId,
    buildChatPath,
  );
  const { mutate: followTurn } = useFollowAiScriptExplanationChatTurn(
    businessId,
    buildChatPath,
  );
  const {
    data: threadsData,
    error: threadsError,
    refetch: refetchThreads,
  } = useGetAiScriptExplanationChatThreads(businessId, runId, buildChatPath);
  const isAccessDenied = isChatAccessDeniedError(threadsError);
  const {
    data: threadData,
    isPending: isThreadPending,
    refetch: refetchThread,
  } = useGetAiScriptExplanationChatThread(
    businessId,
    runId,
    isAccessDenied ? null : selectedThreadId,
    buildChatPath,
  );

  const threads = isAccessDenied ? [] : (threadsData?.threads ?? []);
  const selectedThread = isAccessDenied
    ? null
    : (threads.find((thread) => thread.id === selectedThreadId) ?? null);
  const selectedThreadFromShow =
    !isAccessDenied && threadData?.thread.id === selectedThreadId
      ? threadData.thread
      : null;
  const selectedThreadLiveState = selectedThreadId
    ? liveStatesByThreadId[selectedThreadId]
    : undefined;
  const displayedLiveText = selectedThreadLiveState?.displayedText ?? "";
  const progressSteps = selectedThreadLiveState?.progressSteps ?? [];
  // Settle events can arrive in the same poll batch as the final text deltas,
  // flipping isBusy off while the typewriter is still behind targetText. The
  // catch-up animation and the live bubble must outlive isBusy, or the final
  // flush (gated on displayed === target) never runs and the reply vanishes.
  const hasUnplayedLiveText = selectedThreadLiveState
    ? selectedThreadLiveState.displayedText.length <
      selectedThreadLiveState.targetText.length
    : false;
  const hasRunningThread =
    Object.keys(localRunningTurnsByThreadId).length > 0 ||
    threads.some(
      (thread) =>
        thread.running ||
        !!thread.active_turn ||
        !!localRunningTurnsByThreadId[thread.id],
    );
  const isSelectedThreadRunning = selectedThreadId
    ? !!(
        selectedThreadLiveState?.isFollowing ||
        selectedThreadFromShow?.running ||
        selectedThreadFromShow?.active_turn ||
        selectedThread?.running ||
        selectedThread?.active_turn ||
        localRunningTurnsByThreadId[selectedThreadId]
      )
    : isStartingNewTurn;
  const isBusy =
    view === CHAT_VIEW.list ? isStartingNewTurn : isSelectedThreadRunning;
  // The query's own state, not "a thread is selected and the transcript is
  // empty". Inferring it from the message count meant a history fetch that
  // failed left a spinner running forever, because a failed query has no
  // messages either.
  const isThreadHistoryLoading =
    view === CHAT_VIEW.chat && !!selectedThreadId && isThreadPending;
  const attachedContext =
    selectionContext && hasSelectionContextContent(selectionContext)
      ? selectionContext
      : null;

  useEffect(() => {
    selectedThreadIdRef.current = selectedThreadId;
  }, [selectedThreadId]);

  const updateThreadLiveState = useCallback(
    (
      threadId: string,
      updater: (state: ThreadLiveState) => ThreadLiveState,
    ) => {
      setLiveStatesByThreadId((currentStates) => ({
        ...currentStates,
        [threadId]: updater(currentStates[threadId] ?? emptyThreadLiveState()),
      }));
    },
    [],
  );

  const markThreadFollowing = useCallback(
    (threadId: string, isFollowing: boolean) => {
      updateThreadLiveState(threadId, (state) => ({
        ...state,
        isFollowing,
      }));
    },
    [updateThreadLiveState],
  );

  const appendLiveTextDelta = useCallback(
    (threadId: string, delta: string) => {
      updateThreadLiveState(threadId, (state) => ({
        ...state,
        targetText: state.targetText + delta,
      }));
    },
    [updateThreadLiveState],
  );

  const appendLiveProgressStep = useCallback(
    (threadId: string, label: string) => {
      updateThreadLiveState(threadId, (state) => ({
        ...state,
        progressSteps: appendProgressStep(state.progressSteps, label),
      }));
    },
    [updateThreadLiveState],
  );

  const finishThreadTurn = useCallback(
    (threadId: string) => {
      setLocalRunningTurnsByThreadId((currentTurns) =>
        removeThreadTurn(currentTurns, threadId),
      );
      markThreadFollowing(threadId, false);
    },
    [markThreadFollowing],
  );

  const setPendingThreadResponse = useCallback(
    (threadId: string, pendingResponse: PendingChatResponse) => {
      updateThreadLiveState(threadId, (state) => ({
        ...state,
        pendingResponse,
        isFollowing: false,
      }));
    },
    [updateThreadLiveState],
  );

  const clearLiveResponse = useCallback((threadId?: string | null) => {
    if (!threadId) return;

    setLiveStatesByThreadId((currentStates) => ({
      ...currentStates,
      [threadId]: {
        ...(currentStates[threadId] ?? emptyThreadLiveState()),
        targetText: "",
        displayedText: "",
        progressSteps: [],
        pendingResponse: null,
      },
    }));
  }, []);

  useEffect(() => {
    loadedThreadIdRef.current = null;
    hasAutoEnteredChatRef.current = false;
    wasAtBottomRef.current = true;
    setView(CHAT_VIEW.list);
    setInput("");
    setMessages([]);
    setSelectedThreadId(null);
    setLiveStatesByThreadId({});
    setLocalRunningTurnsByThreadId({});
    setIsStartingNewTurn(false);
    followedTurnKeysRef.current.clear();
    // buildChatPath belongs here because the query keys are derived from the
    // path it builds, not from the ids.
  }, [businessId, runId, buildChatPath]);

  useEffect(() => {
    if (!pendingQuestion) return;
    setInput(pendingQuestion);
    textareaRef.current?.focus();
    onQuestionPrepared?.();
  }, [pendingQuestion, onQuestionPrepared]);

  useEffect(() => {
    if (isAccessDenied || hasAutoEnteredChatRef.current || !threadsData) return;
    hasAutoEnteredChatRef.current = true;
    if (threadsData.threads.length === 0) setView(CHAT_VIEW.chat);
  }, [isAccessDenied, threadsData]);

  useEffect(() => {
    if (!hasRunningThread) return undefined;

    const interval = setInterval(() => {
      void refetchThreads();
    }, THREAD_LIST_POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [hasRunningThread, refetchThreads]);

  // Seed the transcript from persisted history when entering a thread. The
  // in-session transcript stays authoritative afterwards (it carries steps and
  // selection-context chips that aren't persisted). `view` is a dependency
  // because reopening the same thread keeps the query key (and threadData
  // reference) unchanged, so the view transition is the only signal to reseed.
  useEffect(() => {
    if (view !== CHAT_VIEW.chat) return;
    if (!threadData || threadData.thread.id !== selectedThreadId) return;
    if (loadedThreadIdRef.current === threadData.thread.id) return;

    loadedThreadIdRef.current = threadData.thread.id;
    setMessages(threadData.messages.map(mapPersistedMessage));
    if (!threadData.thread.running && !threadData.thread.active_turn) {
      setLiveStatesByThreadId((currentStates) => ({
        ...currentStates,
        [threadData.thread.id]: emptyThreadLiveState(),
      }));
    }
  }, [threadData, selectedThreadId, view]);

  useEffect(() => {
    const activeTurn = selectedThreadFromShow?.active_turn;
    if (!businessId || !selectedThreadId || !activeTurn) return;

    const turnKey = `${selectedThreadId}:${activeTurn.turn_id}`;
    if (followedTurnKeysRef.current.has(turnKey)) return;

    followedTurnKeysRef.current.add(turnKey);
    setLocalRunningTurnsByThreadId((currentTurns) => ({
      ...currentTurns,
      [selectedThreadId]: activeTurn.turn_id,
    }));
    markThreadFollowing(selectedThreadId, true);

    followTurn(
      {
        runId,
        threadId: selectedThreadId,
        turnId: activeTurn.turn_id,
        onTextDelta: (delta) => {
          appendLiveTextDelta(selectedThreadId, delta);
        },
        onProgress: (label) => {
          appendLiveProgressStep(selectedThreadId, label);
        },
      },
      {
        onSuccess: ({ response, threadId }) => {
          finishThreadTurn(threadId);
          setPendingThreadResponse(threadId, response);
        },
        onError: (error: unknown) => {
          const message = getErrorMessage(error);
          followedTurnKeysRef.current.delete(turnKey);
          finishThreadTurn(selectedThreadId);
          clearLiveResponse(selectedThreadId);
          if (selectedThreadIdRef.current !== selectedThreadId) return;

          setMessages((currentMessages) => [
            ...currentMessages,
            buildAssistantErrorMessage(message),
          ]);
        },
      },
    );
  }, [
    businessId,
    appendLiveProgressStep,
    appendLiveTextDelta,
    clearLiveResponse,
    finishThreadTurn,
    followTurn,
    markThreadFollowing,
    runId,
    selectedThreadFromShow?.active_turn,
    selectedThreadId,
    setPendingThreadResponse,
  ]);

  // Track whether the user is at the bottom of the chat scroll viewport.
  useEffect(() => {
    if (view !== CHAT_VIEW.chat) return undefined;

    const viewport = bottomRef.current?.closest(
      "[data-radix-scroll-area-viewport]",
    );
    if (!(viewport instanceof HTMLElement)) return undefined;

    const handleScroll = () => {
      wasAtBottomRef.current =
        viewport.scrollTop + viewport.clientHeight >=
        viewport.scrollHeight - 80;
    };
    viewport.addEventListener("scroll", handleScroll, { passive: true });
    return () => viewport.removeEventListener("scroll", handleScroll);
  }, [view]);

  // Follow new content only while pinned to the bottom.
  useEffect(() => {
    if (!wasAtBottomRef.current) return;
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [messages, isBusy, displayedLiveText, progressSteps]);

  useEffect(() => {
    if (!isBusy && !hasUnplayedLiveText) return undefined;

    const interval = setInterval(() => {
      if (!selectedThreadId) return;

      updateThreadLiveState(selectedThreadId, (state) => {
        if (state.displayedText.length >= state.targetText.length) return state;

        const remaining = state.targetText.length - state.displayedText.length;
        const step = Math.max(2, Math.ceil(remaining / 40));
        return {
          ...state,
          displayedText: state.targetText.slice(
            0,
            state.displayedText.length + step,
          ),
        };
      });
    }, 24);

    return () => clearInterval(interval);
  }, [isBusy, hasUnplayedLiveText, selectedThreadId, updateThreadLiveState]);

  useEffect(() => {
    if (!selectedThreadId || !selectedThreadLiveState?.pendingResponse) return;
    const pendingResponse = selectedThreadLiveState.pendingResponse;

    if (
      selectedThreadLiveState.displayedText.length <
      selectedThreadLiveState.targetText.length
    )
      return;

    setMessages((currentMessages) => [
      ...currentMessages,
      buildAssistantResponseMessage(
        pendingResponse,
        selectedThreadLiveState.progressSteps,
      ),
    ]);
    clearLiveResponse(selectedThreadId);
  }, [selectedThreadId, selectedThreadLiveState, clearLiveResponse]);

  const resizeTextarea = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, 160)}px`;
  };

  const handleDraftChange = (value: string) => {
    setInput(value);
    resizeTextarea();
  };

  const resetComposer = () => {
    setInput("");
    if (textareaRef.current) textareaRef.current.style.height = "auto";
  };

  const handleOpenThread = (threadId: string) => {
    wasAtBottomRef.current = true;
    loadedThreadIdRef.current = null;
    setMessages([]);
    resetComposer();
    // Same-thread reopen keeps the query key, so nothing refetches on its own;
    // other threads refetch automatically when their (stale) key remounts.
    if (threadId === selectedThreadIdRef.current) void refetchThread();
    setSelectedThreadId(threadId);
    setView(CHAT_VIEW.chat);
  };

  const handleNewChat = () => {
    wasAtBottomRef.current = true;
    setSelectedThreadId(null);
    loadedThreadIdRef.current = null;
    setMessages([]);
    resetComposer();
    setView(CHAT_VIEW.chat);
  };

  const handleBackToList = () => {
    setView(CHAT_VIEW.list);
  };

  // targetThreadId bypasses selected-thread state for sends that start a
  // brand-new chat (the state update hasn't applied yet in that tick).
  const handleSend = (text?: string, targetThreadId?: string | null) => {
    const trimmedInput = (text ?? input).trim();
    if (!trimmedInput || isBusy) return;
    let activeThreadId =
      targetThreadId === undefined ? selectedThreadId : targetThreadId;

    const apiContent = attachedContext
      ? `${trimmedInput}\n\n${formatSelectionContextSentence(attachedContext)}`
      : trimmedInput;

    // Sending re-pins the transcript to the bottom.
    wasAtBottomRef.current = true;
    if (activeThreadId) {
      markThreadFollowing(activeThreadId, true);
    } else {
      setIsStartingNewTurn(true);
    }
    setMessages((currentMessages) => [
      ...currentMessages,
      {
        id: buildMessageId(),
        role: "user",
        content: trimmedInput,
        apiContent,
        selectionContext: attachedContext,
      },
    ]);
    resetComposer();

    sendMessage(
      {
        runId,
        message: apiContent,
        threadId:
          targetThreadId === undefined ? selectedThreadId : targetThreadId,
        onThread: (thread, turnId) => {
          activeThreadId = thread.id;
          setIsStartingNewTurn(false);
          // Mark the turn as followed up front: the show query reports it as
          // active_turn while it streams, and the reattach effect would
          // otherwise open a second stream for a turn this send already owns.
          followedTurnKeysRef.current.add(`${thread.id}:${turnId}`);
          loadedThreadIdRef.current = thread.id;
          setSelectedThreadId(thread.id);
          void refetchThreads();
          setLocalRunningTurnsByThreadId((currentTurns) => ({
            ...currentTurns,
            [thread.id]: turnId,
          }));
          markThreadFollowing(thread.id, true);
        },
        onTextDelta: (delta) => {
          const threadId = activeThreadId;
          if (!threadId) return;

          appendLiveTextDelta(threadId, delta);
        },
        onProgress: (label) => {
          const threadId = activeThreadId;
          if (!threadId) return;

          appendLiveProgressStep(threadId, label);
        },
      },
      {
        onSuccess: ({ response, thread, turnId }) => {
          setIsStartingNewTurn(false);
          followedTurnKeysRef.current.add(`${thread.id}:${turnId}`);
          finishThreadTurn(thread.id);
          setPendingThreadResponse(thread.id, response);
        },
        onError: (error: unknown) => {
          const message = getErrorMessage(error);
          const threadId = activeThreadId;
          setIsStartingNewTurn(false);
          if (threadId) {
            finishThreadTurn(threadId);
            clearLiveResponse(threadId);
          }
          if (threadId && selectedThreadIdRef.current !== threadId) return;

          setMessages((currentMessages) => [
            ...currentMessages,
            buildAssistantErrorMessage(message),
          ]);
        },
      },
    );
  };

  // From the list view: typing a question starts a fresh chat with it.
  const handleStartNewChatWithMessage = (text?: string) => {
    const trimmedInput = (text ?? input).trim();
    if (!trimmedInput || isBusy) return;

    setSelectedThreadId(null);
    loadedThreadIdRef.current = null;
    setMessages([]);
    setView(CHAT_VIEW.chat);
    handleSend(trimmedInput, null);
  };

  const getIsThreadRunning = (threadId: string) => {
    const thread = threads.find(
      (candidateThread) => candidateThread.id === threadId,
    );
    const liveState = liveStatesByThreadId[threadId];

    return !!(
      liveState?.isFollowing ||
      thread?.running ||
      thread?.active_turn ||
      localRunningTurnsByThreadId[threadId]
    );
  };

  return {
    isAccessDenied,
    isListView: view === CHAT_VIEW.list,
    input,
    messages,
    threads,
    getIsThreadRunning,
    selectedThread,
    attachedContext,
    isBusy,
    isThreadHistoryLoading,
    displayedLiveText,
    progressSteps,
    bottomRef,
    textareaRef,
    handleDraftChange,
    handleOpenThread,
    handleNewChat,
    handleStartNewChatWithMessage,
    handleBackToList,
    handleSend,
  };
};
